import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Calendar, ChevronLeft, ChevronRight, AlertTriangle, Clock, Gavel, FileText, Shield, Users, Loader2, AlertCircle, Filter, ExternalLink } from "lucide-react";
import HRLayout from "../components/HRLayout";
import { hrDeadlines, formatApiError } from "../lib/api";

const MONTH_NAMES = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const WEEK_DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const TYPE_META = {
    td_12_mesi: { icon: FileText, color: "bg-amber-500", label: "TD 12 mesi" },
    td_24_mesi: { icon: FileText, color: "bg-red-500", label: "TD 24 mesi" },
    scadenza_contratto: { icon: FileText, color: "bg-blue-500", label: "Fine contratto" },
    visita_medica_scadenza: { icon: Shield, color: "bg-emerald-500", label: "Visita medica" },
    formazione_sicurezza_scadenza: { icon: Shield, color: "bg-emerald-600", label: "Formazione" },
    udienza: { icon: Gavel, color: "bg-indigo-600", label: "Udienza" },
    disciplinare_giustificazioni: { icon: AlertTriangle, color: "bg-rose-600", label: "Discipl. 5gg" },
    dvr_data_revisione: { icon: Shield, color: "bg-red-700", label: "DVR" },
    rspp_nomina_data: { icon: Shield, color: "bg-red-700", label: "RSPP" },
    rls_nomina_data: { icon: Shield, color: "bg-red-700", label: "RLS" },
    addetti_antincendio_scadenza: { icon: Shield, color: "bg-red-700", label: "Antincendio" },
    addetti_primo_soccorso_scadenza: { icon: Shield, color: "bg-red-700", label: "Primo soccorso" },
};

const typeMeta = (tipo) => TYPE_META[tipo] || { icon: Clock, color: "bg-slate-500", label: tipo };

export default function HRCalendar() {
    const today = new Date();
    const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterTypes, setFilterTypes] = useState(new Set());

    const monthStart = useMemo(() => new Date(cursor.getFullYear(), cursor.getMonth(), 1), [cursor]);
    const monthEnd = useMemo(() => new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0), [cursor]);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const fromISO = new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1).toISOString().slice(0, 10);
                const toISO = new Date(cursor.getFullYear(), cursor.getMonth() + 2, 0).toISOString().slice(0, 10);
                const data = await hrDeadlines(fromISO, toISO);
                setEvents(data.events || []);
            } catch (err) {
                setError(formatApiError(err));
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [cursor]);

    // Build calendar grid: Monday-first, 42 cells
    const grid = useMemo(() => {
        const firstDay = monthStart.getDay(); // 0=Sun
        const offset = (firstDay + 6) % 7; // make Monday=0
        const cells = [];
        for (let i = 0; i < 42; i++) {
            const d = new Date(monthStart);
            d.setDate(d.getDate() - offset + i);
            cells.push(d);
        }
        return cells;
    }, [monthStart]);

    const eventsByDate = useMemo(() => {
        const m = {};
        events.forEach((e) => {
            if (filterTypes.size && !filterTypes.has(e.tipo)) return;
            const k = e.data;
            if (!m[k]) m[k] = [];
            m[k].push(e);
        });
        return m;
    }, [events, filterTypes]);

    const monthEvents = useMemo(() => {
        const fromISO = monthStart.toISOString().slice(0, 10);
        const toISO = monthEnd.toISOString().slice(0, 10);
        return events
            .filter((e) => e.data >= fromISO && e.data <= toISO)
            .filter((e) => !filterTypes.size || filterTypes.has(e.tipo))
            .sort((a, b) => a.data.localeCompare(b.data));
    }, [events, filterTypes, monthStart, monthEnd]);

    const todayISO = new Date().toISOString().slice(0, 10);

    const uniqueTypes = useMemo(() => Array.from(new Set(events.map((e) => e.tipo))), [events]);

    const toggleType = (t) => {
        setFilterTypes((prev) => {
            const n = new Set(prev);
            if (n.has(t)) n.delete(t); else n.add(t);
            return n;
        });
    };

    return (
        <HRLayout
            breadcrumbs={[{ label: "Calendario Scadenze" }]}
            title="Calendario Scadenze"
            subtitle="Vista cronologica cross-cliente di scadenze normative, udienze e procedimenti disciplinari."
            actions={
                <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 shadow-xs">
                    <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-600" data-testid="btn-prev-month">
                        <ChevronLeft size={14} />
                    </button>
                    <button onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))} className="h-7 px-3 text-xs font-semibold text-slate-700 hover:bg-slate-100 rounded-md" data-testid="btn-today">
                        Oggi
                    </button>
                    <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-600" data-testid="btn-next-month">
                        <ChevronRight size={14} />
                    </button>
                    <div className="pl-2 pr-1 text-sm font-semibold text-slate-900">
                        {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
                    </div>
                </div>
            }
        >
            {/* Type filters */}
            {uniqueTypes.length > 0 && (
                <div className="hr-card shadow-xs p-3 flex items-center gap-2 flex-wrap">
                    <Filter size={13} className="text-slate-400 ml-2" />
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Filtri tipo</span>
                    {uniqueTypes.map((t) => {
                        const meta = typeMeta(t);
                        const Icon = meta.icon;
                        const active = filterTypes.has(t);
                        return (
                            <button
                                key={t}
                                onClick={() => toggleType(t)}
                                data-testid={`cal-filter-${t}`}
                                className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-md text-[11px] font-semibold transition-colors ${
                                    active ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                }`}
                            >
                                <span className={`w-2 h-2 rounded-full ${meta.color}`} />
                                <Icon size={11} />
                                {meta.label}
                            </button>
                        );
                    })}
                    {filterTypes.size > 0 && (
                        <button onClick={() => setFilterTypes(new Set())} className="text-[11px] text-slate-500 hover:text-slate-900 font-medium ml-2">
                            Reset
                        </button>
                    )}
                </div>
            )}

            {/* Loading / Error */}
            {loading && <div className="hr-card p-16 text-center"><Loader2 size={20} className="animate-spin mx-auto text-slate-400" /></div>}
            {error && <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm flex items-start gap-2"><AlertCircle size={16} /> {error}</div>}

            {!loading && !error && (
                <>
                    {/* Month grid */}
                    <div className="hr-card shadow-xs overflow-hidden" data-testid="hr-calendar-grid">
                        <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
                            {WEEK_DAYS.map((d) => (
                                <div key={d} className="px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-500 text-center">{d}</div>
                            ))}
                        </div>
                        <div className="grid grid-cols-7">
                            {grid.map((d, i) => {
                                const iso = d.toISOString().slice(0, 10);
                                const inMonth = d.getMonth() === cursor.getMonth();
                                const isToday = iso === todayISO;
                                const dayEvents = eventsByDate[iso] || [];
                                return (
                                    <div
                                        key={i}
                                        className={`min-h-[96px] border-r border-b border-slate-100 p-1.5 ${
                                            inMonth ? "bg-white" : "bg-slate-50/50"
                                        } ${isToday ? "ring-1 ring-blue-500 ring-inset" : ""}`}
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <span className={`text-[11px] font-semibold ${isToday ? "inline-flex items-center justify-center w-5 h-5 bg-blue-600 text-white rounded-full" : inMonth ? "text-slate-700" : "text-slate-300"}`}>
                                                {d.getDate()}
                                            </span>
                                            {dayEvents.length > 2 && (
                                                <span className="text-[9px] text-slate-500 font-mono">+{dayEvents.length}</span>
                                            )}
                                        </div>
                                        <div className="space-y-0.5">
                                            {dayEvents.slice(0, 3).map((e, j) => {
                                                const meta = typeMeta(e.tipo);
                                                return (
                                                    <Link
                                                        key={j}
                                                        to={`/admin/hr/clients/${e.client_id}`}
                                                        className={`block px-1.5 py-0.5 rounded text-[10px] text-white truncate hover:opacity-90 ${meta.color}`}
                                                        title={`${e.client_name} — ${e.lavoratore}: ${e.descrizione}`}
                                                        data-testid={`cal-event-${iso}-${j}`}
                                                    >
                                                        {e.lavoratore || e.client_name}
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Month events list */}
                    <div className="hr-card shadow-xs overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/80 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Calendar size={14} className="text-slate-600" />
                                <h3 className="text-sm font-semibold text-slate-900">Eventi del mese</h3>
                            </div>
                            <span className="text-[11px] text-slate-500 font-medium">{monthEvents.length} eventi</span>
                        </div>
                        {monthEvents.length === 0 ? (
                            <div className="p-12 text-center text-sm text-slate-500">Nessun evento in {MONTH_NAMES[cursor.getMonth()]}.</div>
                        ) : (
                            <div className="divide-y divide-slate-100">
                                {monthEvents.map((e, i) => {
                                    const meta = typeMeta(e.tipo);
                                    const Icon = meta.icon;
                                    return (
                                        <div key={i} className="px-6 py-3 flex items-center gap-4 hover:bg-slate-50/50" data-testid={`cal-list-${i}`}>
                                            <div className="w-14 text-right flex-shrink-0">
                                                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{MONTH_NAMES[new Date(e.data).getMonth()].slice(0, 3)}</div>
                                                <div className="text-xl font-bold font-mono text-slate-900">{new Date(e.data).getDate()}</div>
                                            </div>
                                            <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${meta.color} text-white flex-shrink-0`}>
                                                <Icon size={14} />
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-semibold text-slate-900">{e.lavoratore || e.client_name}</span>
                                                    <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${e.severita === "alta" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{e.severita}</span>
                                                </div>
                                                <div className="text-[11px] text-slate-600 mt-0.5">{e.descrizione}</div>
                                                <Link to={`/admin/hr/clients/${e.client_id}`} className="text-[11px] text-blue-600 hover:underline mt-0.5 inline-flex items-center gap-1">
                                                    <Users size={10} /> {e.client_name}
                                                </Link>
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                                <div className={`text-sm font-mono font-bold ${e.giorni_residui < 0 ? "text-red-700" : e.giorni_residui <= 7 ? "text-amber-700" : "text-slate-700"}`}>
                                                    {e.giorni_residui < 0 ? `${Math.abs(e.giorni_residui)}gg fa` : `+${e.giorni_residui}gg`}
                                                </div>
                                            </div>
                                            <Link to={`/admin/hr/clients/${e.client_id}`} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-400 hover:text-slate-900">
                                                <ExternalLink size={13} />
                                            </Link>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </>
            )}
        </HRLayout>
    );
}
